import * as React from 'react';
import ViewColumnOutlined from '@mui/icons-material/ViewColumnOutlined';
import { alpha, Box, Button as MaterialButton, ClickAwayListener } from '@mui/material';

import { CheckBox, Text, Tooltip } from 'ui';
import palette from 'ui/theme/palette';

export interface TableColumnsButtonColumn {
  id: string;
  label: string;
}

interface Props {
  /** Hideable columns to list in the dropdown, in display order. */
  columns: TableColumnsButtonColumn[];
  hiddenColumns: string[];
  onChange: (hiddenColumns: string[]) => void;
  /** Renders as a circular icon button (matching FilterToggleButton) instead of a labeled button. */
  iconOnly?: boolean;
  dataCy?: string;
}

/**
 * "Columns" button opening a checkbox list that shows/hides table columns; pair with the Table
 * organism's `hideable`/`hiddenColumns` props. Non-modal like FilterDropdown.
 */
export const TableColumnsButton: React.FC<Props> = (props) => {
  const [open, setOpen] = React.useState(false);

  // Filled green styling signals that some columns are hidden; stale ids that no longer match a
  // listed column don't count. The count also goes into the accessible name, so the state doesn't
  // rely on color alone.
  const hiddenCount = props.columns.filter((column) => props.hiddenColumns.includes(column.id)).length;
  const hasHiddenColumns = hiddenCount > 0;
  const label = hasHiddenColumns ? `Columns — ${hiddenCount} hidden` : 'Columns';

  let accentColor = hasHiddenColumns ? 'primary.dark' : 'text.secondary';
  let borderColor = hasHiddenColumns ? palette.primary.dark : palette.text.disabled;
  if (open) {
    accentColor = 'primary.main';
    borderColor = palette.primary.main;
  }
  const fillColor = hasHiddenColumns ? palette.primary.light : palette.background.paper;

  const toggleColumn = (id: string) => {
    props.onChange(
      props.hiddenColumns.includes(id)
        ? props.hiddenColumns.filter((columnId) => columnId !== id)
        : [...props.hiddenColumns, id]
    );
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)} mouseEvent={'onMouseUp'}>
      <Box sx={{ position: 'relative' }}>
        {props.iconOnly ? (
          <Tooltip title={label}>
            <Box
              onClick={() => setOpen(!open)}
              data-cy={props.dataCy ?? 'table-columns-button'}
              aria-label={label}
              role="button"
              sx={{
                borderRadius: '50%',
                border: `1px solid ${borderColor}`,
                boxShadow: open ? `0px 0px 0px 3px ${alpha(palette.primary.main, 0.25)}` : '0px',
                background: fillColor,
                height: '36px',
                width: '36px',
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover svg': { color: 'primary.main' }
              }}
            >
              <ViewColumnOutlined sx={{ fontSize: '20px', color: accentColor }} />
            </Box>
          </Tooltip>
        ) : (
          <MaterialButton
            variant="outlined"
            startIcon={<ViewColumnOutlined />}
            onClick={() => setOpen(!open)}
            data-cy={props.dataCy ?? 'table-columns-button'}
            aria-label={label}
            sx={{
              height: '36px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 400,
              borderRadius: '6px',
              color: open || hasHiddenColumns ? accentColor : 'text.primary',
              borderColor,
              bgcolor: fillColor,
              '&:hover': { bgcolor: fillColor }
            }}
          >
            Columns
          </MaterialButton>
        )}
        {open && (
          <Box data-cy="table-columns-dropdown" sx={styles.dropdown}>
            <Text
              type="body3"
              content="Show Columns"
              sx={{
                pt: 0.5,
                px: 2.5,
                pb: 1,
                fontSize: '11px',
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            />
            {props.columns.map((column) => (
              <Box
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                data-cy="table-columns-option"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  py: 1,
                  px: 2.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'background.secondary' }
                }}
              >
                <CheckBox
                  name={column.id}
                  labelPlacement="end"
                  standalone
                  value={!props.hiddenColumns.includes(column.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => toggleColumn(column.id)}
                />
                <Text type="body2" content={column.label} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

// Positioning and elevation match FilterDropdown so the two header dropdowns read as one family.
const styles = {
  dropdown: {
    position: 'absolute',
    right: 0,
    mt: 1,
    minWidth: '226px',
    py: 1,
    background: '#fff',
    zIndex: '1000',
    boxShadow: 8,
    borderRadius: '6px'
  }
};
